import { addOrUpdateBooking } from "@/APIs/Booking/UpdateBooking";
import { DiaryContextType } from "@/context/DiaryContext";
import { ViewBookingAssignedAreasInner } from "collins-js-sdk";
import { AreaFromApi, UndoableAssignment } from "./SpaceScanner.types";

// The SDK types area id/name as object; undo sends full area objects, matching Stage 2 apply.
const toViewAssignedAreas = (
  areas: AreaFromApi[]
): ViewBookingAssignedAreasInner[] =>
  areas as unknown as ViewBookingAssignedAreasInner[];

export interface UndoOptimisationResult {
  revertedBookingIds: string[];
  failures: Array<{ assignment: UndoableAssignment; error: string }>;
}

export const undoOptimisation = async ({
  assignments,
  diaryContext,
  onProgress
}: {
  assignments: UndoableAssignment[];
  diaryContext: DiaryContextType;
  onProgress?: (processed: number) => void;
}): Promise<UndoOptimisationResult> => {
  const revertedBookingIds: string[] = [];
  const failures: UndoOptimisationResult["failures"] = [];
  let processed = 0;

  for (const assignment of assignments) {
    const baseBooking = diaryContext.bookings?.find(
      (booking) => booking.id === assignment.bookingId
    );

    if (!baseBooking) {
      failures.push({
        assignment,
        error: "Booking is no longer in the diary"
      });
      processed++;
      onProgress?.(processed);
      continue;
    }

    try {
      const response = await addOrUpdateBooking({
        booking: {
          ...baseBooking,
          assignedAreas: toViewAssignedAreas(assignment.previousAreas)
        },
        diaryContext
      });

      if (!response || response.unsaved) {
        failures.push({
          assignment,
          error: "Failed to revert booking"
        });
      } else {
        revertedBookingIds.push(assignment.bookingId);
      }
    } catch (error) {
      failures.push({
        assignment,
        error:
          error instanceof Error ? error.message : "Failed to revert booking"
      });
    }

    processed++;
    onProgress?.(processed);
  }

  return { revertedBookingIds, failures };
};
